import ChartDataLabels from 'chartjs-plugin-datalabels';
import { getLocaleFormat, formatLargeNumber } from 'utils/numbers';
import { getVar, brightenColor, getRandomHexadecimalColor } from 'utils/colors';
import { CHART_TYPES } from '../constants';

const calculateFontSize = ({ container, width, maxSize }) => {
    if (!container || !container?.offsetWidth || width) return null;
    const containerWidth = container?.offsetWidth || width;
    let size = Math.max(Math.floor(containerWidth / 50), 10);
    const innerMaxSize = maxSize || 12;
    if (size > innerMaxSize) size = innerMaxSize;
    return size;
};

const addTransparency = ({ hexColor, opacity = 1 }) => {
    const red = parseInt(hexColor.substring(1, 3), 16);
    const green = parseInt(hexColor.substring(3, 5), 16);
    const blue = parseInt(hexColor.substring(5, 7), 16);

    return ` rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

const isColorDark = (rgbColor) => {
    const grayscaleValue = 0.299 * rgbColor[0] + 0.587 * rgbColor[1] + 0.114 * rgbColor[2];
    const threshold = 170; // # of 255

    if (grayscaleValue < threshold) return true;
    else return false;
};

const getLegendPlugins = ({ type, data, theme, container }) => {
    const onHoverLegend = (event, chartElement) => {
        event.native.target.style.cursor = 'pointer';
    };
    const onLeaveLegend = (event, chartElement) => {
        event.native.target.style.cursor = 'default';
    };

    let legendProps = {};

    if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie) {
        legendProps = {
            position: 'left',
            align: 'center',
        };
    }

    return {
        legend: {
            onHover: onHoverLegend,
            onLeave: onLeaveLegend,
            labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 10,
                boxHeight: 10,
                padding: 10,
                font: (context) => ({
                    family: theme.typography.subtitle.fontFamily,
                    size: calculateFontSize({ container }),
                }),
            },
            ...legendProps,
        },
    };
};

const getDefineExtraPluggins = ({ type, t, theme, container, locale }) => {
    let plugins = [];

    if (type === CHART_TYPES.doughnut) {
        plugins.push({
            id: 'customDoughnutText',
            afterDraw(chart) {
                let ctx = chart.ctx;
                ctx.save();

                // Calculate the center
                let centerX = (chart.chartArea.left + chart.chartArea.right) / 2;
                let centerY = (chart.chartArea.top + chart.chartArea.bottom) / 2;

                let sum = chart.data.datasets[0].data.reduce((a, b) => a + b, 0);

                let text = formatLargeNumber({ number: sum, locale });

                const fontSize = calculateFontSize({ container, maxSize: 18 });

                let totalFont = `bold ${fontSize}px ${theme.typography.h6.fontFamily}`;

                ctx.font = totalFont;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.fillStyle = getVar(theme.colors.grey[700]);

                // First text with total
                ctx.fillText(text, centerX, centerY);

                const textFont = `${theme.typography.subtitle.fontSize}px ${theme.typography.subtitle.fontFamily}`;

                ctx.font = textFont;
                ctx.fillStyle = getVar(theme.colors.grey[500]);

                // Second text with label
                ctx.fillText(t('label_total'), centerX, centerY + fontSize + 4);

                ctx.restore();
            },
        });
    }

    return plugins;
};

const getColorPalette = (theme) => {
    return [
        '#7BA4FE',
        '#FFC766',
        '#FF9E82',
        '#AEE5F8',
        '#F8DC63',
        '#E5A5CE',
        '#5EEBE5',
        '#A7E675',
        '#245DFD',
        '#FF8C00',
        '#FF3B30',
        '#76BDE8',
        '#E9B200',
        '#AA5B9C',
        '#00A8BE',
        '#4CAD20',
        '#1234B6',
        '#B75400',
        '#B7182A',
        '#3B71A7',
        '#A77700',
        '#792D7A',
        '#006388',
        '#237C10',
    ];
};

let previousColors = [];

const changeData = ({ type, data, bright, alpha, theme }) => {
    const hasMultipleDatasets = data.datasets.length > 1;
    const colorPalette = getColorPalette(theme);

    const getColor =
        ({ datasetIndex, isBorder }) =>
        (color, index) => {
            if (index <= colorPalette.length - 1) {
                let colorIndex = index % colorPalette.length;
                if (hasMultipleDatasets) {
                    colorIndex = datasetIndex % colorPalette.length;
                }

                let newColor = colorPalette[colorIndex];

                if (type === CHART_TYPES.radar) {
                    if (!isBorder) {
                        newColor = addTransparency({ hexColor: newColor, opacity: 0.5 });
                    } else {
                        newColor = addTransparency({ hexColor: newColor, opacity: 0.6 });
                    }
                }
                return newColor;
            } else {
                const newColor = getRandomHexadecimalColor(previousColors);
                previousColors.push(newColor);
                return brightenColor({ color: newColor, bright, alpha });
            }
        };

    const newData = {
        ...data,
        datasets: data.datasets.map((dataset, datasetIndex) => {
            const backgroundColor = (dataset.backgroundColor || ['rgb(0,0,0)'])?.map(
                getColor({ datasetIndex }),
            );
            const newDataset = {
                ...dataset,
                backgroundColor,
                borderColor: (dataset.borderColor || ['rgb(0,0,0)'])?.map(
                    getColor({ datasetIndex, isBorder: true }),
                ),
            };

            if (type === CHART_TYPES.radar) {
                newDataset.fill = true;
                newDataset.borderWidth = 3;
                newDataset.pointBackgroundColor = backgroundColor;
                newDataset.pointBorderWidth = 2;
            } else if (type === CHART_TYPES.area) {
                const isLast = datasetIndex === data.datasets.length - 1;
                newDataset.fill = isLast ? 'origin' : datasetIndex + 1;
            }
            return newDataset;
        }),
    };

    return newData;
};

const getScales = ({ type, container, data }) => {
    const xGrid = {
        display: false,
    };

    const yGrid = {
        display: true,
    };

    if (type === CHART_TYPES.hBar) {
        xGrid.display = true;
        yGrid.display = false;
    }

    let scales = {
        x: {
            grid: xGrid,
            ticks: {
                font: (context) => {
                    const size = calculateFontSize({ container });
                    return {
                        size: size,
                    };
                },
                format: {
                    notation: 'compact', // Use compact notation for large numbers
                    compactDisplay: 'short', // Display as short (e.g., 1K, 1M)
                },
            },
        },
        y: {
            grid: yGrid,
            ticks: {
                font: (context) => {
                    const size = calculateFontSize({ container });
                    return {
                        size: size,
                    };
                },
                format: {
                    notation: 'compact', // Use compact notation for large numbers
                    compactDisplay: 'short', // Display as short (e.g., 1K, 1M)
                },
            },
        },
    };

    if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie || type === CHART_TYPES.radar) {
        scales = {
            x: { display: false, y: { display: false } },
        };
    }

    if (type === CHART_TYPES.radar) {
        scales.r = {
            ticks: {
                display: false,
            },
        };
    }

    return scales;
};

const adjustPaddingBasedOnLabel = ({ data, container, theme, padding }) => {
    const dataset = data.datasets[0].data;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    ctx.font = `${calculateFontSize({ container })}px ${theme.typography.subtitle.fontFamily}`;
    let maxWidth = 0;

    dataset.forEach((value) => {
        const width = ctx.measureText(value.toLocaleString()).width;
        if (width > maxWidth) {
            maxWidth = width;
        }
    });

    const right = maxWidth + 10;

    return {
        ...padding,
        right,
    };
};

export const buildChartConfig = ({
    type,
    data,
    options = {},
    theme,
    t,
    isWidget,
    container,
    locale,
    onClick,
}) => {
    let customOptions = {};

    let anchor = 'end';
    let offset = -4;
    let align = 'top';
    let padding = {
        top: 0,
        right: 6,
        bottom: 0,
        left: 6,
    };
    let elements = {
        bar: {
            borderRadius: 4,
        },
    };

    if (type === CHART_TYPES.hBar) {
        customOptions = { indexAxis: 'y' };
        anchor = 'end';
        align = 'end';
        offset = 4;
        padding = adjustPaddingBasedOnLabel({ data, container, theme, padding });
    } else if (type === CHART_TYPES.doughnut || type === CHART_TYPES.pie) {
        anchor = 'center';
        offset = 0;
        align = 'center';
    }

    let baseOptions = {
        ...options,
        ...customOptions,
        responsive: true,
        animation: {
            duration: 0,
        },
        maintainAspectRatio: false,
        devicePixelRatio: window.devicePixelRatio || 1,
        layout: { padding },
        elements,
        onClick: (event, chartElement) => {
            if (chartElement.length > 0) {
                const element = chartElement[0];
                const datasetIndex = element.datasetIndex;
                const index = element.index;
                const label = data.labels[index];
                const value = data.datasets[datasetIndex].data[index];

                onClick && onClick({ element, index, label, value });
            }
        },
        onHover: (event, elements) => {
            if (!onClick) return;

            const canvas = event.native.target;

            if (elements.length > 0) {
                canvas.style.cursor = 'pointer';
            } else {
                canvas.style.cursor = 'default';
            }
        },
        scales: getScales({ type, container, data }),
        plugins: {
            customCanvasBackgroundColor: {
                color: getVar(theme.colors.primary.white),
            },
            subtitle: {
                text: data?.subtitle,
                display: data?.subtitle || false,
                font: {
                    size: calculateFontSize({ container }),
                },
            },
            tooltip: {
                enabled: true,
                callbacks: {
                    title: function (tooltipItem) {
                        const label = tooltipItem.label;
                        return label;
                    },
                    label: function (tooltipItem) {
                        let value = tooltipItem.dataset.data?.[tooltipItem.dataIndex];
                        if (isNaN(value)) return '';
                        value = getLocaleFormat(value, locale, {
                            minimumFractionDigits: 0,
                            maximumFfractionDigits: 2,
                        });
                        const label = tooltipItem?.dataset?.label || '';
                        if (label) return `${label}: ${value}`;
                        return value;
                    },
                },
                boxPadding: 4,
            },
            datalabels: {
                color: (context) => {
                    const backgroundColors = context?.dataset?.backgroundColor || [];
                    const backgroundColor = backgroundColors?.[context.dataIndex];
                    const isChartPie =
                        type === CHART_TYPES.pie || type === CHART_TYPES.doughnut || false;
                    if (!backgroundColor?.length || !isChartPie)
                        return getVar(theme.colors.grey[700]);

                    const darks = backgroundColors.filter((current) => {
                        const rgbString = current;
                        let rgbArray = rgbString.match(/\d+/g);
                        if (!rgbArray) return false;
                        rgbArray = rgbArray.map(function (value) {
                            return parseInt(value, 10);
                        });
                        return isColorDark(rgbArray);
                    });

                    return darks.length >= Math.ceil(backgroundColors?.length / 2)
                        ? getVar(theme.colors.primary.white)
                        : getVar(theme.colors.grey[700]);
                },
                font: (context) => {
                    const size = calculateFontSize({ container });

                    return {
                        family: theme.typography.subtitle.fontFamily,
                        size,
                        weight: 'bold',
                    };
                },
                display: (context) => {
                    const value = context.dataset.data[context.dataIndex];
                    return value !== 0;
                },
                anchor,
                offset,
                align,
                clip: false,
                formatter: (value) => {
                    if (isNaN(value)) return value;
                    return getLocaleFormat(value, locale, {
                        minimumFractionDigits: 0,
                        maximumFfractionDigits: 2,
                    });
                },
                textAlign: 'center',
            },
            ...getLegendPlugins({ type, data, theme, container }),
        },
    };

    let finalType = '';

    switch (type) {
        case CHART_TYPES.hBar:
            finalType = CHART_TYPES.bar;
            break;
        case CHART_TYPES.area:
            finalType = CHART_TYPES.line;
            break;
        case CHART_TYPES.bar:
        default:
            finalType = type;
    }

    const getShouldDisplayLegend = ({ chart }) => {
        if (!chart?.chartArea?.width || !chart?.legend?.legendItems) return false;
        const chartWidth = chart.chartArea.width;
        const legendItems = chart.legend.legendItems;

        const minChartWidth = 125;
        const maxLegendItems = 16;

        if (chartWidth < minChartWidth || legendItems >= maxLegendItems) return false;
        return true;
    };

    const finalChartConfig = {
        type: finalType?.toLowerCase(),
        data: changeData({ type, data, bright: 10, alpha: 1, theme }),
        options: { ...baseOptions },
        plugins: [
            ChartDataLabels,
            {
                id: 'customCanvasBackgroundColor',
                beforeDraw: (chart, args, options) => {
                    const { ctx } = chart;
                    ctx.save();
                    ctx.globalCompositeOperation = 'destination-over';
                    ctx.fillStyle = options.color || getVar(theme.colors.primary.white);
                    ctx.fillRect(0, 0, chart.width, chart.height);
                    ctx.restore();
                },
            },
            {
                id: 'legendMargin',
                beforeInit: (chart) => {
                    const originalFit = chart.legend.fit;

                    chart.legend.fit = function fit() {
                        originalFit.bind(chart.legend)();
                        this.height += 16;
                    };
                },
            },
            {
                id: 'toggleLegendPlugin',
                beforeRender(chart) {
                    if (type !== CHART_TYPES.doughnut && type !== CHART_TYPES.pie) return;
                    chart.options.plugins.legend.display = getShouldDisplayLegend({
                        chart,
                    });
                },
                resize(chart) {
                    if (type !== CHART_TYPES.doughnut && type !== CHART_TYPES.pie) return;
                    chart.options.plugins.legend.display = getShouldDisplayLegend({
                        chart,
                    });

                    chart.update();
                },
            },
            ...getDefineExtraPluggins({ type, t, theme, container, locale }),
        ],
    };

    return finalChartConfig;
};
